if (!customElements.get('range-slider')) {
  customElements.define(
    'range-slider',
    class RangeSliderComponent extends HTMLElement {
      constructor() {
        super();

        this.resizeFilters = window.theme.debounce(this.handleResize.bind(this), 50);
        this.onLayoutChange = () => this.handleLayoutChange();
        this.onMoveEvent = (event) => this.onMove(event);
        this.onStopEvent = (event) => this.onStop(event);
        this.onStartEvent = (event) => this.onStart(event);
        this.startX = 0;
        this.x = 0;
        this.step = 0.0;
        // normalize flag
        this.normalizeFact = 20;
      }

      connectedCallback() {
        // retrieve touch button
        this.touchLeft = this.querySelector('[data-range-left]');
        this.touchRight = this.querySelector('[data-range-right]');
        this.lineSpan = this.querySelector('[data-range-line]');

        // get some properties
        this.min = parseFloat(this.getAttribute('data-se-min'));
        this.max = parseFloat(this.getAttribute('data-se-max'));

        // retrieve default values
        let defaultMinValue = this.min;
        if (this.hasAttribute('data-se-min-value')) {
          defaultMinValue = parseFloat(this.getAttribute('data-se-min-value'));
        }
        let defaultMaxValue = this.max;

        if (this.hasAttribute('data-se-max-value')) {
          defaultMaxValue = parseFloat(this.getAttribute('data-se-max-value'));
        }

        // check values are correct
        if (defaultMinValue < this.min) {
          defaultMinValue = this.min;
        }

        if (defaultMaxValue > this.max) {
          defaultMaxValue = this.max;
        }

        if (defaultMinValue > defaultMaxValue) {
          defaultMinValue = defaultMaxValue;
        }

        this.defaultMinValue = defaultMinValue;
        this.defaultMaxValue = defaultMaxValue;

        if (this.getAttribute('data-se-step')) {
          this.step = Math.abs(parseFloat(this.getAttribute('data-se-step')));
        }

        document.addEventListener('theme:resize', this.onLayoutChange);

        // link events
        this.touchLeft.addEventListener('mousedown', this.onStartEvent);
        this.touchRight.addEventListener('mousedown', this.onStartEvent);
        this.touchLeft.addEventListener('touchstart', this.onStartEvent);
        this.touchRight.addEventListener('touchstart', this.onStartEvent);

        // collection-filters.css is loaded async; Safari can measure unstyled thumbs
        this.resizeObserver = new ResizeObserver(this.onLayoutChange);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.touchLeft);

        this.syncLayout();
        requestAnimationFrame(() => this.syncLayout());
      }

      hasValidLayout() {
        if (!this.touchLeft || this.offsetWidth <= 0) return false;

        // Unstyled dots are block-level and match the slider width (~1873px in Safari)
        return getComputedStyle(this.touchLeft).position === 'absolute' && this.touchLeft.offsetWidth > 0 && this.touchLeft.offsetWidth < this.offsetWidth;
      }

      handleLayoutChange() {
        if (!this.classList.contains('is-initialized')) {
          this.syncLayout();
          return;
        }

        this.resizeFilters();
      }

      syncLayout() {
        if (this.classList.contains('is-initialized') || !this.hasValidLayout()) return;

        this.reset();

        // usefull values, min, max, normalize fact is the width of both touch buttons
        this.maxX = this.offsetWidth - this.touchRight.offsetWidth;
        this.selectedTouch = null;
        this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;

        this.setMinValue(this.defaultMinValue);
        this.setMaxValue(this.defaultMaxValue);
        this.classList.add('is-initialized');
      }

      handleResize() {
        if (!this.hasValidLayout()) return;

        // Store current min and max values before resize
        const minValue = parseFloat(this.getAttribute('data-se-min-value'));
        const maxValue = parseFloat(this.getAttribute('data-se-max-value'));

        // Get the new container width
        const newWidth = this.offsetWidth;
        this.maxX = newWidth - this.touchRight.offsetWidth;
        this.initialValue = newWidth - this.touchLeft.offsetWidth - this.normalizeFact;

        // Reposition the thumbs based on the current values
        this.setMinValue(minValue);
        this.setMaxValue(maxValue);
      }

      reset() {
        this.touchLeft.style.left = '0px';

        this.touchRight.style.left = this.offsetWidth - this.touchLeft.offsetWidth + 'px';
        this.lineSpan.style.marginLeft = '0px';
        this.lineSpan.style.width = this.offsetWidth - this.touchLeft.offsetWidth + 'px';
        this.startX = 0;
        this.x = 0;

        this.maxX = this.offsetWidth - this.touchRight.offsetWidth;
        this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;
      }

      setMinValue(minValue) {
        const ratio = (minValue - this.min) / (this.max - this.min);
        this.touchLeft.style.left = Math.ceil(ratio * (this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact))) + 'px';
        this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + 'px';
        this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + 'px';
        this.setAttribute('data-se-min-value', minValue);
      }

      setMaxValue(maxValue) {
        const ratio = (maxValue - this.min) / (this.max - this.min);
        this.touchRight.style.left = Math.ceil(ratio * (this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact)) + this.normalizeFact) + 'px';
        this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + 'px';
        this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + 'px';
        this.setAttribute('data-se-max-value', maxValue);
      }

      onStart(event) {
        // Prevent default dragging of selected content
        event.preventDefault();
        let eventTouch = event;

        if (event.touches) {
          eventTouch = event.touches[0];
        }

        if (event.currentTarget === this.touchLeft) {
          this.x = this.touchLeft.offsetLeft;
        } else if (event.currentTarget === this.touchRight) {
          this.x = this.touchRight.offsetLeft;
        }

        this.startX = eventTouch.pageX - this.x;
        this.selectedTouch = event.currentTarget;
        document.addEventListener('mousemove', this.onMoveEvent);
        document.addEventListener('mouseup', this.onStopEvent);
        document.addEventListener('touchmove', this.onMoveEvent);
        document.addEventListener('touchend', this.onStopEvent);
      }

      onMove(event) {
        let eventTouch = event;

        if (event.touches) {
          eventTouch = event.touches[0];
        }

        this.x = eventTouch.pageX - this.startX;

        if (this.selectedTouch === this.touchLeft) {
          if (this.x > this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10) {
            this.x = this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10;
          } else if (this.x < 0) {
            this.x = 0;
          }

          this.selectedTouch.style.left = this.x + 'px';
        } else if (this.selectedTouch === this.touchRight) {
          if (this.x < this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10) {
            this.x = this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10;
          } else if (this.x > this.maxX) {
            this.x = this.maxX;
          }
          this.selectedTouch.style.left = this.x + 'px';
        }

        // update line span
        this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + 'px';
        this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + 'px';

        // write new value
        this.calculateValue();

        // call on change
        if (this.getAttribute('on-change')) {
          const fn = new Function('min, max', this.getAttribute('on-change'));
          fn(this.getAttribute('data-se-min-value'), this.getAttribute('data-se-max-value'));
        }

        this.onChange(this.getAttribute('data-se-min-value'), this.getAttribute('data-se-max-value'));
      }

      onStop(event) {
        document.removeEventListener('mousemove', this.onMoveEvent);
        document.removeEventListener('mouseup', this.onStopEvent);
        document.removeEventListener('touchmove', this.onMoveEvent);
        document.removeEventListener('touchend', this.onStopEvent);

        this.selectedTouch = null;

        // write new value
        this.calculateValue();

        // call did changed
        this.onChanged(this.getAttribute('data-se-min-value'), this.getAttribute('data-se-max-value'));
      }

      onChange(min, max) {
        const rangeHolder = this.closest('[data-range-holder]');
        if (rangeHolder) {
          const priceMin = rangeHolder.querySelector('[data-field-price-min]');
          const priceMax = rangeHolder.querySelector('[data-field-price-max]');

          if (priceMin && priceMax) {
            priceMin.value = parseInt(min);
            priceMax.value = parseInt(max);
          }
        }
      }

      onChanged(min, max) {
        if (this.hasAttribute('data-range-filter-update')) {
          this.dispatchEvent(new CustomEvent('theme:filter:range-update', {bubbles: true}));
        }
      }

      calculateValue() {
        const newValue = (this.lineSpan.offsetWidth - this.normalizeFact) / this.initialValue;
        let minValue = this.lineSpan.offsetLeft / this.initialValue;
        let maxValue = minValue + newValue;

        minValue = minValue * (this.max - this.min) + this.min;
        maxValue = maxValue * (this.max - this.min) + this.min;

        if (this.step !== 0.0) {
          let multi = Math.floor(minValue / this.step);
          minValue = this.step * multi;

          multi = Math.floor(maxValue / this.step);
          maxValue = this.step * multi;
        }

        if (this.selectedTouch === this.touchLeft) {
          this.setAttribute('data-se-min-value', minValue);
        }

        if (this.selectedTouch === this.touchRight) {
          this.setAttribute('data-se-max-value', maxValue);
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        document.removeEventListener('theme:resize', this.onLayoutChange);
        document.removeEventListener('mousemove', this.onMoveEvent);
        document.removeEventListener('mouseup', this.onStopEvent);
        document.removeEventListener('touchmove', this.onMoveEvent);
        document.removeEventListener('touchend', this.onStopEvent);
        this.touchLeft.removeEventListener('mousedown', this.onStartEvent);
        this.touchRight.removeEventListener('mousedown', this.onStartEvent);
        this.touchLeft.removeEventListener('touchstart', this.onStartEvent);
        this.touchRight.removeEventListener('touchstart', this.onStartEvent);
      }
    }
  );
}
